import React, { useRef, useState } from 'react';

import dynamic from 'next/dynamic';

import { GETAPP_PRICING_OPTIONS } from 'bx-forms/eas/getapp';
import Accordion from 'components/Accordion';

const InlineForm = dynamic(
  () => import('bx-forms/eas/getapp').then((mod) => mod.InlineForm),
  { ssr: false }
);
import Grid, { Container } from 'components/Grid';
import ImpressionEvent from 'components/ImpressionEvent';
import OneTapScript from 'components/OneTapScript';
import Paper from 'components/Paper';
import SplashElement from 'components/SplashElement';
import Typography from 'components/Typography';
import { useAppContext } from 'context/AppContext';
import { useEventContext } from 'context/EventContext';
import { PreloadProvider } from 'context/PreloadContext';
import { useUser } from 'hooks';
import useViewport from 'hooks/useViewport';
import useLasForm from 'screens/serp/hooks/useLasForm';

import PopularComparisons from '../PopularComparisons';
import BestProductCard from './components/BestProductCard';
import BestProductCardDetailed from './components/BestProductCardDetailed';
import FilteredListings from './components/FilteredListings';
import KeyFeatures from './components/KeyFeatures';
import TableOfContent from './components/TableOfContent';
import PPLBanner from './components/TableOfContent/components/PPLBanner';
import TitleCR from './components/TitleCR';
import Top4 from './components/Top4';
import Top10 from './components/Top10';
import { SORT_OPTIONS } from './components/Top10/components/SortSelect/constants';
import TopFeaturedProducts from './components/TopFeaturedProducts';

import classes from './SerpContentRich.module.scss';

/** @type {import("./types").default} */
const SerpContentRich = ({
  serpData: {
    category,
    feature,
    key_features,
    facets,
    listings,
    pagination,
    comparisons,
    topFeaturedProducts,
    topRatedProducts: topRatedProductsParam,
  },
}) => {
  const [topRatedProducts, setTopRatedProducts] = useState(
    topRatedProductsParam
  );

  const { up } = useViewport();
  const { sendEvent } = useEventContext();
  const { head } = useAppContext();
  const stickyContainerRef = useRef();
  const isDesktop = up('md');
  const [user] = useUser();

  const hasPPLExpertForm = head?.hasPplExpertFormEnabled;
  const currentFacet = feature;
  const headerTop10 = `${topRatedProducts?.length} Best ${category.name} Software with ${currentFacet.label}`;
  const hasComparisons = comparisons?.length > 0;
  const hasKeyfeatures = key_features?.length > 0;
  const hasTopFeaturedSection =
    topFeaturedProducts?.length > 0 &&
    topFeaturedProducts.every((product) => product.is_premium);
  const shouldRenderTop4Component =
    topRatedProducts?.length >= 3 && topRatedProducts?.length <= 4;

  const TABLE_OF_CONTENT = [
    {
      menuTitle: `${topRatedProducts.length} best products`,
      id: 'best-products',
    },
    hasTopFeaturedSection && {
      menuTitle: 'Top featured products',
      id: 'featured-products',
    },
    {
      menuTitle: `Details for ${topRatedProducts.length} best products`,
      id: 'best-products-details',
    },
    { menuTitle: 'Other top products', id: 'other' },
    hasKeyfeatures && {
      menuTitle: `${category.name} key features`,
      id: 'key-features',
    },
    hasComparisons
      ? { menuTitle: 'Popular comparisons', id: 'popular-comparisons' }
      : {},
  ].filter(({ id }) => id);

  const handleTableOfContentExpand = (_, { isExpanded }) => {
    sendEvent(
      isExpanded
        ? {
            name: 'engagement_navigation',
            engagementComponent: 'table-of-contents',
            engagementDestination: 'open_table-of-content',
            engagementDetails: 'expand_table-of-content',
          }
        : {
            name: 'engagement_navigation',
            engagementComponent: 'table-of-contents',
            engagementDetails: 'close_table-of-content',
            engagementDestination: 'collapse_table-of-content',
          }
    );
  };

  useLasForm({
    hasPplExitIntentEnabled: head?.hasPplExitIntentEnabled,
    global_category_id: category.global_category_id,
    isPPL: true,
  });

  const onShowPricingModal = useLasForm({
    hasPplExitIntentEnabled: false,
    global_category_id: category.global_category_id,
    isPPL: true,
    config: {
      formTemplate: 'price',
    },
    form: 'openLasModal',
    hasCallFunction: true,
  });

  const handleSorting = (sort) => {
    let sortedProducts = [...topRatedProductsParam];
    if (sort === SORT_OPTIONS.USER_RATING) {
      sortedProducts = [...topRatedProductsParam].sort(
        (a, b) => b.rating_avg - a.rating_avg
      );
    }
    if (sort === SORT_OPTIONS.REVIEWS_COUNT) {
      sortedProducts = [...topRatedProductsParam].sort(
        (a, b) => b.num_reviews - a.num_reviews
      );
    }
    setTopRatedProducts(sortedProducts);
  };

  return (
    <>
      <SplashElement />
      <OneTapScript user={user} />
      <div className={classes.root}>
        <Container fixedWidth={true} className={classes.pageContentContainer}>
          {!isDesktop && (
            <TitleCR
              title={head.page_header}
              lastUpdate={category.last_updated_on}
              keyFeatures={key_features}
            />
          )}
          <Grid container={true} justifyContent="center" spacing={3}>
            {isDesktop ? (
              <Grid
                item={true}
                md={4}
                lg={3}
                className={classes.stickyContainer}
              >
                <div ref={stickyContainerRef}>
                  <div>
                    <TableOfContent
                      content={TABLE_OF_CONTENT}
                      stickyContainerRef={stickyContainerRef}
                    />
                  </div>
                </div>
              </Grid>
            ) : (
              <Grid item={true} xs={12}>
                <Paper>
                  <Accordion
                    onExpandClick={handleTableOfContentExpand}
                    summary={
                      <Typography variant="heading-sm">
                        Table of contents
                      </Typography>
                    }
                  >
                    <TableOfContent content={TABLE_OF_CONTENT} />
                  </Accordion>
                </Paper>
              </Grid>
            )}
            <Grid item={true} xs={12} md={8} lg={9}>
              {isDesktop && (
                <TitleCR
                  title={head.page_header}
                  lastUpdate={category.last_updated_on}
                  keyFeatures={key_features}
                />
              )}

              <section id="best-products" className="ContentItem">
                <Typography component="h2" className={classes.title}>
                  {headerTop10}
                </Typography>
                {shouldRenderTop4Component ? (
                  <Top4
                    listings={topRatedProducts}
                    category={category}
                    facet={currentFacet}
                  />
                ) : (
                  <Top10
                    listings={topRatedProducts}
                    category={category}
                    hasDemoForm={head.hasPplDemoFormEnabled}
                    facet={currentFacet}
                    onShowPricingModal={onShowPricingModal}
                    handleSortSelect={handleSorting}
                  />
                )}
              </section>

              {hasTopFeaturedSection && (
                <section id="featured-products" className="ContentItem">
                  <TopFeaturedProducts
                    listings={topFeaturedProducts.slice(0, 3)}
                  />
                </section>
              )}

              <section id="best-products-details" className="ContentItem">
                <Typography component="h2" className={classes.title}>
                  Details for the {topRatedProducts.length} best products
                </Typography>

                {topRatedProducts.map((listing, index) => (
                  <React.Fragment key={listing.id}>
                    {shouldRenderTop4Component ? (
                      <BestProductCardDetailed
                        id={`listing-${listing.slug}`}
                        listing={listing}
                        facet={currentFacet}
                        categoryName={category.name}
                        hasDemoForm={head.hasPplDemoFormEnabled}
                        onShowPricingModal={onShowPricingModal}
                        position={index}
                      />
                    ) : (
                      <BestProductCard
                        id={`listing-${listing.slug}`}
                        listing={listing}
                        isFirstCard={index === 0}
                        facet={currentFacet}
                        hasDemoForm={head.hasPplDemoFormEnabled}
                        onShowPricingModal={onShowPricingModal}
                        position={index}
                      />
                    )}
                  </React.Fragment>
                ))}
              </section>

              {hasPPLExpertForm && !isDesktop && (
                <ImpressionEvent
                  event={{
                    engagementComponent: 'ppl-banner',
                    engagementDestination: 'banner_displayed',
                    engagementDetails: 'ppl-banner',
                    name: 'section_impression',
                  }}
                >
                  <PPLBanner category={category.name} />
                </ImpressionEvent>
              )}

              <section id="other" className="ContentItem">
                <PreloadProvider>
                  <FilteredListings
                    listings={listings}
                    pagination={pagination}
                    facets={facets}
                    category={category}
                    hasDemoForm={head.hasPplDemoFormEnabled}
                    onShowPricingModal={onShowPricingModal}
                  />
                </PreloadProvider>
              </section>

              <InlineForm
                formId="6SLWtXqH3GlxoaFs7U12Nh"
                gatewayUrl="https://www.capterra.com/bx-capterra-api/bx-forms/submit"
                formTemplate="INLINE_CARD"
                qualificationOptions={GETAPP_PRICING_OPTIONS}
                step2QuestionLabel="Which pricing model do you prefer?"
                headline="Top-rated software of 2026"
                subHeading="Fill out the form and we'll send a list of the top-ranked software based on real user reviews directly to your inbox."
                buttonText="SEND ME THE LIST"
                successHeadline="Thank you, enjoy!"
                successMessage="Your list will be delivered to your inbox shortly."
                step2Subtitle="Thank you! Let us know which pricing model you prefer and we'll send a list of tools with the right pricing available."
                contextProps={{
                  pageType: 'directory',
                  pagePlacement: 'inline',
                }}
                tracking={{
                  formName: 'DMBUY_2022_GA_Blog_Acq',
                  contentCategory: 'Directory_Inline',
                  contentRequest: 'Top Software',
                }}
              />

              {hasKeyfeatures && (
                <section id="key-features" className="ContentItem">
                  <KeyFeatures
                    categoryName={category.name}
                    keyFeatures={key_features}
                  />
                </section>
              )}

              {!!hasComparisons && (
                <section id="popular-comparisons" className="ContentItem">
                  <Typography component="h2" className={classes.title}>
                    {`Popular ${category.name.toLowerCase()} comparisons`}
                  </Typography>
                  <PopularComparisons comparisons={comparisons} />
                </section>
              )}
            </Grid>
          </Grid>
        </Container>
      </div>
    </>
  );
};

export default SerpContentRich;
